import { motion } from "framer-motion";
import { useAnimateOnScroll } from "Hooks/useAnimateOnScroll";
import { fadeIn, staggerContainer, scaleIn } from "../Common/fadeStyles";
import {
  Target,
  FileText,
  MessageSquare,
  CheckCircle,
  Calculator,
  User,
} from "lucide-react";
import { Card, Carousel } from "antd";
import { StyledButton as Button } from "Components/StyledButton";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "Features/Routes/routeConstants";
import "./styles.css";

export const Features = () => {
  const navigate = useNavigate();
  const [aiToolsRef, aiToolsControls] = useAnimateOnScroll();
  return (
    <motion.section
      // @ts-ignore
      ref={aiToolsRef}
      initial="hidden"
      // @ts-ignore
      animate={aiToolsControls}
      variants={fadeIn}
      className="py-10 md:py-16 w-full"
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-8 w-[90%]">
        <motion.div variants={fadeIn} className="text-center mb-10 md:mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Smarter, Not Harder: Let AI Guide You
          </h2>
        </motion.div>
        <motion.div
          variants={staggerContainer}
          className="max-w-7xl mx-auto mb-10 h-full w-full"
        >
          <Carousel
            arrows
            dots={false}
            centerMode
            centerPadding="20px"
            slidesToShow={3}
            slidesToScroll={1}
            className="w-full features-carousel"
            responsive={[
              {
                breakpoint: 1024,
                settings: {
                  slidesToShow: 2,
                  slidesToScroll: 1,
                },
              },
              {
                breakpoint: 768,
                settings: {
                  slidesToShow: 1,
                  slidesToScroll: 1,
                },
              },
            ]}
          >
            {[
              {
                icon: Target,
                title: "College Fit Score",
                description: "AI analyzes your profile for perfect matches",
              },
              {
                icon: FileText,
                title: "SoP & LoR Builder",
                description: "AI-powered application writing assistant",
              },
              {
                icon: MessageSquare,
                title: "AI Chat Assistant",
                description: "Ask anything about studying abroad",
              },
              {
                icon: CheckCircle,
                title: "Readiness Score",
                description: "Track your application progress",
              },
              {
                icon: User,
                title: "Personalized Recommendations",
                description: "Get personalized recommendations",
              },
              {
                icon: Calculator,
                title: "Loan ROI Calculator",
                description: "Calculate your ROI for studying abroad",
              },
            ].map((tool, index) => (
              <Card
                className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full w-full"
                key={index}
              >
                <div className="h-full">
                  {/* Background animation elements */}
                  <div className="absolute inset-0 opacity-10">
                    <motion.div
                      animate={{
                        scale: [1, 1.2, 1],
                        rotate: [0, 180, 360],
                      }}
                      transition={{
                        duration: 8,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: "linear",
                      }}
                      className="w-32 h-32 border-2 border-[#80E280] rounded-full absolute -top-8 -right-8"
                    />
                    <motion.div
                      animate={{
                        y: [0, -10, 0],
                        opacity: [0.3, 0.7, 0.3],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: "easeInOut",
                        delay: index * 0.5,
                      }}
                      className="w-4 h-4 bg-[#80E280] rounded-full absolute top-4 left-4"
                    />
                  </div>

                  {/* Main tool icon with student interaction animation */}
                  <motion.div
                    whileHover={{ rotate: 10 }}
                    className="w-16 h-16 bg-[#80E280] rounded-full mx-auto mb-4 flex items-center justify-center group-hover:bg-[#70D270] transition-colors relative"
                  >
                    <tool.icon className="w-8 h-8 text-white" />

                    {/* Student avatar animation */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0, x: 20 }}
                      animate={{
                        opacity: [0, 1, 1, 0],
                        scale: [0, 0.8, 0.8, 0],
                        x: [20, 0, 0, -20],
                      }}
                      transition={{
                        duration: 4,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: "easeInOut",
                        delay: index * 0.8,
                      }}
                      className="absolute -right-2 -top-2 w-6 h-6 bg-gradient-to-br from-[#80E280] to-[#4A90E2] rounded-full flex items-center justify-center"
                    >
                      <span className="text-white text-xs font-bold">👤</span>
                    </motion.div>

                    {/* Success indicator animation */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{
                        opacity: [0, 0, 1, 1, 0],
                        scale: [0, 0, 1.2, 1, 0],
                      }}
                      transition={{
                        duration: 4,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: "easeInOut",
                        delay: index * 0.8 + 2,
                      }}
                      className="absolute -right-1 -bottom-1 w-4 h-4 bg-[#80E280] rounded-full flex items-center justify-center"
                    >
                      <CheckCircle className="w-3 h-3 text-white" />
                    </motion.div>
                  </motion.div>

                  {/* Animated progress indicators */}
                  <div className="mb-4 space-y-2">
                    {[...Array(3)].map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ width: "0%" }}
                        animate={{
                          width: ["0%", "100%", "100%", "0%"],
                          opacity: [0.3, 0.8, 0.8, 0.3],
                        }}
                        transition={{
                          duration: 4,
                          repeat: Number.POSITIVE_INFINITY,
                          ease: "easeInOut",
                          delay: index * 0.8 + i * 0.3,
                        }}
                        className="h-1 bg-gradient-to-r from-[#80E280] to-[#80E280] rounded-full mx-auto"
                        style={{ maxWidth: `${80 - i * 15}%` }}
                      />
                    ))}
                  </div>

                  <h3 className="font-semibold text-lg mb-2">{tool.title}</h3>
                  <p className="text-gray-600 text-base mb-4">
                    {tool.description}
                  </p>

                  {/* Interactive demo preview */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{
                      opacity: [0, 0, 1, 1, 0],
                      y: [10, 10, 0, 0, 10],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                      delay: index * 0.8 + 1,
                    }}
                    className="text-xs text-[#80E280] font-medium bg-blue-50 px-3 py-1 rounded-full inline-block"
                  >
                    {index === 0 && "95% match found!"}
                    {index === 1 && "Essay improved!"}
                    {index === 2 && "Question answered!"}
                    {index === 3 && "85% ready!"}
                  </motion.div>

                  {/* Floating particles animation */}
                  {[...Array(3)].map((_, i) => (
                    <motion.div
                      key={`particle-${i}`}
                      animate={{
                        y: [0, -20, 0],
                        x: [0, Math.sin(i) * 10, 0],
                        opacity: [0, 0.6, 0],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: "easeInOut",
                        delay: index * 0.8 + i * 0.5,
                      }}
                      className="absolute w-2 h-2 bg-[#80E280] rounded-full"
                      style={{
                        left: `${20 + i * 25}%`,
                        bottom: `${10 + i * 5}%`,
                      }}
                    />
                  ))}
                </div>
              </Card>
            ))}
          </Carousel>
        </motion.div>
        <motion.div variants={scaleIn} className="text-center">
          <Button
            onClick={() => {
              navigate(ROUTES.COLLEGE_FINDER);
            }}
            size="lg"
            className="bg-[#80E280] hover:bg-[#70D270] transition-all duration-300 hover:scale-105 hover:shadow-lg w-full lg:w-1/3 text-base md:text-lg"
          >
            Get Personalized Recommendations
          </Button>
        </motion.div>
      </div>
    </motion.section>
  );
};
